import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      if (!token) {
        setStatus("error");
        setError("This verification link is invalid.");
        return;
      }
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        checkAuth?.();
      } catch (e) {
        setStatus("error");
        setError(formatApiError(e.response?.data?.detail) || "Verification failed.");
      }
    })();
  }, [token, checkAuth]);

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-6" data-testid="verify-email-page">
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20">
        <Logo imgClassName="h-7" />
      </div>
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8">
          <Logo imgClassName="h-9" clickable={false} />
        </div>

        {status === "loading" && (
          <div className="flex flex-col items-center gap-3" data-testid="verify-loading">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <div className="flex flex-col items-center gap-3" data-testid="verify-success">
            <CheckCircle2 className="h-12 w-12 text-green-600" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-bold tracking-tight">Email verified</h1>
            <p className="text-sm text-muted-foreground">Your account is fully active. You can now tailor CVs, generate cover letters and earn referral rewards.</p>
            <Button asChild className="mt-4 w-full h-11" data-testid="verify-continue-button">
              <Link to="/profile">Continue to Jobsmith</Link>
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-3" data-testid="verify-error">
            <XCircle className="h-12 w-12 text-destructive" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-bold tracking-tight">Verification failed</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button asChild variant="outline" className="mt-4 w-full h-11" data-testid="verify-goto-app-button">
              <Link to="/profile">Go to my account</Link>
            </Button>
            <p className="text-xs text-muted-foreground mt-2">You can request a new link from the banner inside the app.</p>
          </div>
        )}
      </div>
    </div>
  );
}
